import { QueryHookOptions, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { GET_NOTIFICATION_PREFERENCE_QUERY } from 'src/graphql/entities/notification-preference/notifications.operations';
import { NotificationPreferenceResponse } from 'src/graphql/entities/notification-preference/notifications.types';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { QueryParams, QueryVariables, Records } from 'src/graphql/types';

export type GetNotificationPreferenceResponseQuery = {
  getNotificationPreference: Records<NotificationPreferenceResponse>;
};

export const useGetNotificationPreferences = () => {
  const options = useGetNotificationPreferenceQueryParams();
  const { data } = useQuery<GetNotificationPreferenceResponseQuery, QueryVariables>(
    GET_NOTIFICATION_PREFERENCE_QUERY,
    options,
  );
  const notificationPreferences = useMemo(
    () => data?.getNotificationPreference.records || [],
    [data?.getNotificationPreference.records],
  );

  const notificationPreferencesMap = useMemo(
    () =>
      new Map(
        notificationPreferences.map((val) => [
          `${val.notificationEventId}-${val.notificationChannelId}`,
          val,
        ]),
      ),
    [notificationPreferences],
  );

  const getNotificationPreferenceBy = useCallback(
    (eventId: string, channelId: string) => {
      return notificationPreferencesMap.get(`${eventId}-${channelId}`);
    },
    [notificationPreferencesMap],
  );

  return {
    notificationPreferences,
    getNotificationPreferenceBy,
  };
};

export function useGetNotificationPreferenceQueryParams(): QueryHookOptions<
  GetNotificationPreferenceResponseQuery,
  QueryVariables
> {
  const { user } = useCurrentUserProfile();

  const filters = useMemo<QueryParams['filters']>(() => {
    const queryFilters: QueryParams['filters'] = [];
    if (user?.userId) {
      queryFilters.push({
        attribute: 'userId',
        operator: '=',
        value: user?.userId,
      });
    }
    return queryFilters;
  }, [user?.userId]);

  return {
    variables: {
      params: {
        filters,
      },
    },
    context: {
      omitIsolationModeHeader: true,
    },
    skip: !filters?.length,
  };
}
